/**
 * Store Pickup — checkout gate helper.
 *
 * Blocks checkout until the pickup date is filled in, when pickup is the
 * selected delivery method. The real gate is a capture-phase `submit`
 * interceptor: the theme's async validator (custom-async.js /
 * plugin-validator.js) re-enables the submit button on hover/focus of the
 * cart form, so the `disabled` attribute alone cannot be trusted. Button
 * disabling below is cosmetic only.
 *
 * Programmatic form.submit() calls (used by the widget's own validated
 * flow) do not fire the `submit` event, so they are never blocked here.
 */
(function () {
  'use strict';

  var widgetSeen = false; // set once the pickup widget announces itself

  // Message shown when checkout is blocked; override by defining
  // window.crPickupGateMessage before this script runs.
  function gateMessage() {
    return window.crPickupGateMessage || 'Please select a pickup date before checking out.';
  }

  // Is pickup the currently selected delivery method? (Same checks the
  // widget itself uses — crPickupActivated also fires for shipping.)
  function isPickupSelected() {
    if (document.querySelector('.js-toggle-pickup.selected, .pickup-button.selected')) return true;
    var attr = document.getElementById('pickup-attribute');
    return !!(attr && attr.value === '1');
  }

  function pickupFieldsComplete() {
    var date = document.getElementById('pickup-date-start');
    if (!date) return true; // date field not rendered → nothing to require
    return date.value.trim() !== '';
  }

  function gateClosed() {
    return widgetSeen && isPickupSelected() && !pickupFieldsComplete();
  }

  // ---- cosmetic button state ----------------------------------------------

  function checkoutButtons() {
    // Covers the main cart (#checkout) and the side cart, which only has
    // name="checkout" on its button.
    return document.querySelectorAll(
      '#checkout, button[name="checkout"], input[type="submit"][name="checkout"]'
    );
  }

  function syncCheckoutState() {
    var disable = gateClosed();
    checkoutButtons().forEach(function (btn) {
      if (btn.disabled !== disable) btn.disabled = disable;
    });
    // Accelerated checkout buttons (Shop Pay etc.) bypass the cart form —
    // grey them out and block clicks while the gate is closed. (No real
    // `disabled` attribute is possible: Shopify renders them in iframes.)
    document.documentElement.classList.toggle('cr-pickup-gate-closed', disable);
    if (!disable) hideGateError();
  }

  // Re-assert our state whenever the theme's validator (or the widget)
  // strips/sets the disabled attribute.
  var observedButtons = new WeakSet();
  function observeCheckoutButtons() {
    checkoutButtons().forEach(function (btn) {
      if (observedButtons.has(btn)) return;
      observedButtons.add(btn);
      new MutationObserver(syncCheckoutState).observe(btn, {
        attributes: true,
        attributeFilter: ['disabled']
      });
    });
  }

  var style = document.createElement('style');
  style.textContent =
    '.cr-pickup-gate-closed .additional-checkout-buttons,' +
    '.cr-pickup-gate-closed .overlay-dynamic_buy_button,' +
    '.cr-pickup-gate-closed [data-shopify="dynamic-checkout-cart"]' +
    '{ pointer-events: none !important; opacity: .45; filter: grayscale(1); }' +
    '#cr-pickup-gate-error { color: #d20000; margin: 8px 0 0; }';
  document.head.appendChild(style);

  // ---- blocked-submit feedback ---------------------------------------------

  function showGateError() {
    var date = document.getElementById('pickup-date-start');
    var anchor = date ? date.closest('p, div, li') || date : null;
    if (!anchor) return;
    var msg = document.getElementById('cr-pickup-gate-error');
    if (!msg) {
      msg = document.createElement('p');
      msg.id = 'cr-pickup-gate-error';
      anchor.insertAdjacentElement('afterend', msg);
    }
    msg.textContent = gateMessage();
    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideGateError() {
    var msg = document.getElementById('cr-pickup-gate-error');
    if (msg) msg.remove();
  }

  // ---- the hard gate --------------------------------------------------------

  // Capture phase on document runs before the theme's own submit handlers.
  document.addEventListener(
    'submit',
    function (e) {
      if (!gateClosed()) return;
      var form = e.target;
      // Only cart→checkout submits: the theme's cart forms carry a
      // checkout-named control. Let cart "update" submits through.
      if (!form.querySelector('[name="checkout"]')) return;
      if (e.submitter && e.submitter.name && e.submitter.name !== 'checkout') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      showGateError();
      syncCheckoutState();
    },
    true
  );

  // ---- keep cosmetic state fresh --------------------------------------------

  document.addEventListener('crPickupActivated', function () {
    widgetSeen = true;
    observeCheckoutButtons();
    syncCheckoutState();
  });
  document.addEventListener('crDeliveryActivated', function () {
    syncCheckoutState();
  });

  ['change', 'input', 'keyup', 'mouseout'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      if (!widgetSeen || !e.target || !e.target.id) return;
      if (/^(pickup-date-start|note-pickup-date|pickup-first-name|pickup-last-name|pickup-number|pickup-selected-store)$/.test(e.target.id)) {
        syncCheckoutState();
      }
    });
  });

  // Fallback for programmatic value changes (datepicker) that fire no event,
  // and for checkout buttons rendered late (side cart). Cheap, cosmetic only —
  // the submit gate above is what actually enforces the rule.
  setInterval(function () {
    if (!widgetSeen) return;
    observeCheckoutButtons();
    syncCheckoutState();
  }, 1000);
})();
